<template>
  <!-- The wrapper is what the page sizes; the canvas inside is always drawn at
       its natural size and scaled to fit, so a card thumbnail and the live
       preview are the same render at two sizes rather than two markups. -->
  <div ref="frame" class="ktr-frame">
    <!-- The sizer carries the *scaled* dimensions, so the page lays out around
         the rendered size and the canvas can be centred when it is capped below
         its container's width. The canvas is drawn at its natural size inside
         it and scaled from the top-left corner. It is also the card itself —
         the box behind the names, in the design's own colours. -->
    <div
      class="ktr-sizer"
      :class="{ 'ktr-sizer--transparent': isTransparentCard }"
      :style="sizerStyle"
    >
      <div
        ref="canvasEl"
        class="ktr-canvas kb-design-canvas"
        :style="canvasStyle"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div ref="contentEl" class="ktr-content" v-html="renderedHtml"></div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  KABBALAH_CARD_BACKGROUND_COLOR_DEFAULT_N,
  KABBALAH_CARD_BORDER_COLOR_DEFAULT_N,
  KABBALAH_CARD_PADDING_DEFAULT_N,
  KABBALAH_TEMPLATE_CONTENT_WIDTH,
  KABBALAH_TEMPLATE_CANVAS,
  findKabbalahTemplate_N,
  getKabbalahCardPaddingParts_N,
  isTransparentKabbalahColor_N,
  normalizeKabbalahCardPadding_N,
  normalizeKabbalahColor_N,
} from '@/utils/kabbalah_design_templates';
import {
  getKabbalahCardCapacity_N,
  renderKabbalahDesignHtml_N,
} from '@/utils/kabbalah_design_render';
import '@/assets/style/kabbalah_design_canvas.css';

export default {
  name: 'Kabbalah_Template_Render',
  props: {
    templateId: {
      type: String,
      required: true,
    },
    // The design authored in the editor: { html, colours, padding }.
    settings: {
      type: Object,
      required: true,
    },
    // Real records, already reduced to the fields a design can print:
    // { fullName, firstName, lastName, gabbai, duration }.
    // A short list is padded with placeholders so the design still reads the
    // way it will on a full screen.
    entries: {
      type: Array,
      default: () => [],
    },
    // Upper scale limit. Cards are still capped at 1:1 so padding changes keep
    // changing the visible border box instead of being hidden by upscaling.
    maxScale: {
      type: Number,
      default: 1,
    },
    // Editor previews intentionally pad a template with sample rows. The live
    // display disables this so partial cards contain real records only.
    showPlaceholders: {
      type: Boolean,
      default: true,
    },
  },
  // The grid around this card sizes its columns from what the card actually
  // measures, so the gap between cards stays the authored spacing instead of the
  // column's leftover width.
  emits: ['natural-width'],
  data() {
    return {
      scale: 1,
      // The design's real border-box at 1:1, measured from the rendered canvas.
      // Seeded with the original card shape so the first paint has stable space;
      // the next animation frame shrinks it to the actual content plus padding.
      naturalCardWidth: KABBALAH_TEMPLATE_CANVAS.width,
      naturalCardHeight: KABBALAH_TEMPLATE_CANVAS.height,
    };
  },
  computed: {
    template() {
      return findKabbalahTemplate_N(this.templateId);
    },
    canvas() {
      return KABBALAH_TEMPLATE_CANVAS;
    },
    // The card's own colours, from the design. Normalised here rather than
    // trusted: the hex fields are free text, so a half-typed value would
    // otherwise reach the canvas as an invalid style.
    cardBorderColor() {
      return normalizeKabbalahColor_N(
        this.settings?.borderColor,
        KABBALAH_CARD_BORDER_COLOR_DEFAULT_N
      );
    },
    cardBackgroundColor() {
      return normalizeKabbalahColor_N(
        this.settings?.backgroundColor,
        KABBALAH_CARD_BACKGROUND_COLOR_DEFAULT_N
      );
    },
    cardPadding() {
      return normalizeKabbalahCardPadding_N(
        this.settings?.cardPadding ?? KABBALAH_CARD_PADDING_DEFAULT_N
      );
    },
    cardPaddingParts() {
      return getKabbalahCardPaddingParts_N(this.cardPadding);
    },
    maximumCardWidth() {
      return KABBALAH_TEMPLATE_CONTENT_WIDTH + this.cardPaddingParts.horizontal * 2;
    },
    // Whether the design asks to sit straight on whatever is behind it, which
    // the page uses to drop the drop-shadow it would otherwise cast.
    isTransparentCard() {
      return isTransparentKabbalahColor_N(this.cardBackgroundColor);
    },
    // Tracks the measured content, so a design taller than the nominal canvas
    // makes the card grow instead of being cut off at the bottom.
    //
    // The card's colours live here rather than on the scaled canvas inside it:
    // this box is drawn at screen scale, so the border stays a crisp 1px and
    // matches the rounded corners it is clipped to at every thumbnail size.
    sizerStyle() {
      return {
        width: `${this.naturalCardWidth * this.scale}px`,
        height: `${this.naturalCardHeight * this.scale}px`,
        backgroundColor: this.cardBackgroundColor,
        borderColor: this.cardBorderColor,
      };
    },
    canvasStyle() {
      return {
        maxWidth: `${this.maximumCardWidth}px`,
        padding: this.cardPadding,
        transform: `scale(${this.scale})`,
      };
    },
    // How many people this card draws: one per record block the author left in
    // the design. The template's nominal count is only its starting shape — an
    // author who deleted two of three blocks built a one-person card, and
    // padding to three would render sample rows nothing can ever fill.
    cardCapacity() {
      return getKabbalahCardCapacity_N((this.settings && this.settings.html) || '');
    },
    // The entries the design is rendered against — real records first, padded
    // to the number of blocks the design actually carries.
    resolvedEntries() {
      const wanted = this.cardCapacity;
      const filled = this.entries.slice(0, wanted).map((entry) => {
        // A field the record left blank keeps its placeholder in the editor's
        // previews and renders as nothing on the board, which is what
        // `showPlaceholders` decides.
        const textValue = (value, placeholder) => {
          const text = String(value ?? '').trim();
          if (text) return text;
          return this.showPlaceholders ? placeholder : '';
        };

        // Both halves and the joined form, so a design mixing {fullName} with
        // {firstName} draws consistently.
        const firstName = textValue(entry?.firstName, 'First');
        const lastName = textValue(entry?.lastName, 'Last');
        const fullName = textValue(
          entry?.fullName || entry?.name || [entry?.firstName, entry?.lastName]
            .map((part) => String(part ?? '').trim())
            .filter(Boolean)
            .join(' '),
          'Full Name'
        );

        return {
          fullName,
          firstName,
          lastName,
          gabbai: textValue(entry?.gabbai, 'Gabbai'),
          kabbalah: textValue(entry?.kabbalah, 'Kabbalah'),
          duration: textValue(entry?.duration, 'One full year'),
          image: entry?.image || null,
          // Retired tokens, always blank — see KabbalahRenderEntry_N. A design
          // saved while they existed collapses those lines instead of printing
          // `{city}` verbatim.
          city: '',
          beisMedrash: '',
        };
      });

      if (this.showPlaceholders) {
        while (filled.length < wanted) {
          filled.push({
            fullName: 'Full Name',
            firstName: 'First',
            lastName: 'Last',
            gabbai: 'Gabbai',
            kabbalah: 'Kabbalah',
            duration: 'One full year',
            image: null,
            city: '',
            beisMedrash: '',
          });
        }
      }

      return filled;
    },
    // A record carries no picture of its own; only a design can. Kept so an
    // authored <img> that is still the placeholder has something to resolve to.
    leadImage() {
      return this.entries.find((entry) => entry?.image)?.image || null;
    },
    // The authored HTML with its variables resolved. The algorithm lives in a
    // util so it can be exercised without mounting a component.
    renderedHtml() {
      return renderKabbalahDesignHtml_N({
        html: (this.settings && this.settings.html) || '',
        entries: this.resolvedEntries,
        leadImage: this.leadImage,
        removeUnfilledEntries: !this.showPlaceholders,
      });
    },
  },
  methods: {
    // Measuring writes the sizer's height, which resizes the frame that the
    // same observer watches. Doing that inside the observer callback is what
    // makes the browser report "ResizeObserver loop completed with undelivered
    // notifications" — the work is pushed to the next frame instead, and a
    // burst of notifications collapses into a single pass.
    scheduleMeasure() {
      // `measureFrame` is a plain instance property, like `resizeObserver`
      // below: it is scheduling bookkeeping, and making it reactive state would
      // queue a render for nothing.
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measure();
      });
    },
    measure() {
      const frame = this.$refs.frame;
      const canvasEl = this.$refs.canvasEl;
      if (!frame || !canvasEl) return;

      const width = frame.clientWidth;
      const measuredWidth = Math.ceil(canvasEl.offsetWidth || canvasEl.scrollWidth || 0);
      const nextCardWidth = Math.max(measuredWidth, 1);

      if (nextCardWidth !== this.naturalCardWidth) this.naturalCardWidth = nextCardWidth;
      // The unscaled width of the design itself — independent of how wide the
      // frame is, so reporting it can't feed back into the fit it drives.
      if (nextCardWidth !== this.reportedCardWidth) {
        this.reportedCardWidth = nextCardWidth;
        this.$emit('natural-width', nextCardWidth);
      }

      // Zero while the tab is hidden — the last good scale is kept rather than
      // collapsing the canvas, and the observer re-measures once it is shown.
      let nextScale = this.scale;
      if (width) {
        const maxScale = Math.min(Number(this.maxScale) || 1, 1);
        nextScale = Math.min(width / nextCardWidth, maxScale);
      }

      const measuredHeight = Math.ceil(canvasEl.offsetHeight || canvasEl.scrollHeight || 0);
      const nextCardHeight = Math.max(measuredHeight, 1);
      if (nextCardHeight !== this.naturalCardHeight) this.naturalCardHeight = nextCardHeight;

      // Assigned only on a real change: writing the same value would re-render
      // and hand the observer another notification for nothing.
      if (nextScale !== this.scale) this.scale = nextScale;
    },
    // An <img> carries a width but no height, so its real height is only known
    // once it loads — and that is exactly when a design grows past the nominal
    // canvas. ResizeObserver catches it, this is the fallback for browsers
    // without one.
    watchImages() {
      const canvasEl = this.$refs.canvasEl;
      if (!canvasEl) return;
      canvasEl.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', this.scheduleMeasure, { once: true });
        img.addEventListener('error', this.scheduleMeasure, { once: true });
      });
    },
  },
  watch: {
    // Re-measure after the new design has actually been painted.
    renderedHtml() {
      this.$nextTick(() => {
        this.scheduleMeasure();
        this.watchImages();
      });
    },
  },
  mounted() {
    this.measure();
    this.watchImages();
    // The rail, the tab, and the browser window all resize this box, so the fit
    // is observed rather than computed once. The canvas is observed too: that
    // is what reports the design growing — an image finishing loading, a font
    // swapping in, or the author adding a line.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      this.resizeObserver.observe(this.$refs.frame);
      this.resizeObserver.observe(this.$refs.canvasEl);
    } else {
      this.onWindowResize = this.scheduleMeasure;
      window.addEventListener('resize', this.onWindowResize);
    }
  },
  beforeUnmount() {
    if (this.measureFrame) {
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.onWindowResize) {
      window.removeEventListener('resize', this.onWindowResize);
      this.onWindowResize = null;
    }
  },
};
</script>

<style scoped>
.ktr-frame {
  width: 100%;
}

.ktr-sizer {
  position: relative;
  margin: 0 auto;
  /* Clips to the rounded corners drawn on this box. It can no longer cut the
     design off: the height above tracks the measured content. */
  overflow: hidden;
  /* The card. `border-color` and `background-color` come from the design, so
     the width is stated here and never changes — a transparent border still
     occupies its 1px and the layout can't shift when the colour does.
     Deliberately left at the default `content-box`: the scaled canvas is sized
     to this box's content area, so the border sits outside it and can never
     clip the design's edge. */
  border: 1px solid transparent;
  border-radius: 10px;
}

.ktr-canvas {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  padding: 24px 28px;
  width: max-content;
  max-width: 560px;
  box-sizing: border-box;
  /* The card's colour is painted by the sizer above — this box only carries
     the design, so it stays clear of it. */
  background: transparent;
}

.ktr-content {
  width: max-content;
  max-width: 100%;
}
</style>
